import { InfoIcon } from '@carboncopyco/icons';
import { Button, InlineLink, MessageBox, TextBase } from '@carboncopyco/ui';
import { useState } from 'react';
import {
  checkApis,
  measureNetworkPerformance,
} from '../../../util/diagnosticCheck';
import { openLinkInBrowser } from '../../shared/utils';
import { DiagnosticErrorsSection } from './DiagnosticErrorsSection';
// eslint-disable-next-line import/no-cycle
import { DiagnosticSection } from './DiagnosticSection';

export enum DiagnosticCheckTypes {
  Permissions = 'Permissions',
  Network = 'Network',
  Internet_Connection = 'Internet connection',
}

export enum DiagnosticCheckStatus {
  PreCheck = 0,
  DuringCheck = 1,
  AfterCheckSuccessOrOnErrorsView = 2,
  AfterCheckFailed = 3,
}

interface AppDataProps {
  appVersion: string;
  platform: string;
  cpu: string;
  arch: string;
}

interface DiagnosticCheckProps {
  appData: AppDataProps;
  onCloseModal: () => void;
}

const checkNetworkConnection = (): string => {
  const error = 'Poor internet connection: ';
  const { connection } = navigator as Navigator & {
    connection: {
      downlink: number;
      effectiveType: string;
    };
  };
  const { downlink, effectiveType } = connection;
  if (!connection || !downlink || !effectiveType)
    return error.concat('error while checking internet connection');
  const isSlow =
    downlink < 1 || effectiveType === '2g' || effectiveType === '3g';
  return isSlow ? error.concat('check your internet connection') : '';
};

const DiagnosticCheckScreen = ({
  appData,
  onCloseModal,
}: DiagnosticCheckProps) => {
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isFirstRunClicked, setIsFirstRunClicked] = useState<boolean>(false);
  const [isViewErrorsClicked, setIsViewErrorsClicked] =
    useState<boolean>(false);
  const [isNetworkConnectionLoading, setIsNetworkConnectionLoading] =
    useState<boolean>(false);
  const [isPermissionLoading, setIsPermissionLoading] =
    useState<boolean>(false);
  const [isApiLoading, setIsApiLoading] = useState<boolean>(false);

  const reset = () => {
    setErrorMessages([]);
    setIsViewErrorsClicked(false);
  };

  const runDiagnosticHandler = async () => {
    setIsApiLoading(true);
    setIsPermissionLoading(true);
    setIsNetworkConnectionLoading(true);
    reset();
    setIsFirstRunClicked(true);
  };

  const isLoading =
    isApiLoading || isPermissionLoading || isNetworkConnectionLoading;

  const getDiagnosticCheckStatus = (): DiagnosticCheckStatus => {
    if (!isFirstRunClicked) return DiagnosticCheckStatus.PreCheck;
    if (isLoading) return DiagnosticCheckStatus.DuringCheck;
    if (errorMessages.length > 0 && !isViewErrorsClicked)
      return DiagnosticCheckStatus.AfterCheckFailed;
    return DiagnosticCheckStatus.AfterCheckSuccessOrOnErrorsView;
  };

  const checkIfInModes = (modes: DiagnosticCheckStatus[]): boolean =>
    modes.some((mode) => mode === getDiagnosticCheckStatus());

  const isReRun = checkIfInModes([
    DiagnosticCheckStatus.AfterCheckFailed,
    DiagnosticCheckStatus.AfterCheckSuccessOrOnErrorsView,
  ]);

  return (
    <div className="flex flex-col gap-4">
      <div className="gap-5 flex flex-col  pt-2.5 ml-px">
        <TextBase
          label="Having trouble? Quickly identify common issues by running this diagnostics check."
          color="text-ds-natural-600"
        />
        <div className="flex">
          <MessageBox
            border
            borderColor="border-ds-natural-200"
            leftIcon={<InfoIcon />}
            label={`About this device: ${appData.platform} version: ${appData.cpu} | ${appData.arch} | Demostack app version: ${appData.appVersion}️`}
          />
        </div>
      </div>
      <div className="w-[772px] h-[226px]">
        {!isLoading && errorMessages?.length > 0 && isViewErrorsClicked ? (
          <DiagnosticErrorsSection errors={errorMessages} />
        ) : (
          <>
            <DiagnosticSection
              title={DiagnosticCheckTypes.Internet_Connection}
              runningParams={[
                checkNetworkConnection,
                measureNetworkPerformance,
              ]}
              loading={isNetworkConnectionLoading}
              setLoading={setIsNetworkConnectionLoading}
              shouldRun={isFirstRunClicked}
              footerSeparator
              setErrorMessages={setErrorMessages}
            />
            <DiagnosticSection
              title={DiagnosticCheckTypes.Permissions}
              runningParams={[
                window.dsDesktop.diagnosticCheck.checkUserPermissions,
              ]}
              shouldRun={isFirstRunClicked}
              loading={isPermissionLoading}
              footerSeparator
              setLoading={setIsPermissionLoading}
              setErrorMessages={setErrorMessages}
            />
            <DiagnosticSection
              title={DiagnosticCheckTypes.Network}
              runningParams={[
                () => checkApis(true),
                window.dsDesktop.diagnosticCheck.checkApi,
              ]}
              loading={isApiLoading}
              setLoading={setIsApiLoading}
              shouldRun={isFirstRunClicked}
              setErrorMessages={setErrorMessages}
            />
          </>
        )}
      </div>
      <div className="flex flex-row justify-between pt-2">
        <InlineLink
          label="Help center"
          onClick={(e) =>
            openLinkInBrowser(e, 'https://help.demostack.com/en/')
          }
        />
        <div className="flex gap-4">
          {checkIfInModes([
            DiagnosticCheckStatus.PreCheck,
            DiagnosticCheckStatus.DuringCheck,
          ]) && (
            <Button
              link
              label="Cancel"
              onClick={onCloseModal}
            />
          )}

          <Button
            link={isReRun}
            name="primary"
            disabled={isLoading}
            label={`${isReRun ? 'Re-run' : 'Run'} diagnostic`}
            onClick={runDiagnosticHandler}
          />

          {checkIfInModes([DiagnosticCheckStatus.AfterCheckFailed]) ? (
            <Button
              name="Primary"
              label="View errors"
              onClick={() => setIsViewErrorsClicked(true)}
            />
          ) : (
            <>
              {!isLoading && isFirstRunClicked && (
                <Button
                  name="Primary"
                  label="Done"
                  onClick={onCloseModal}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticCheckScreen;
